import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';

import path from 'path';

import { DeleteOutlined as DeleteOutlinedIcon, Edit as EditIcon } from '@mui/icons-material';
import { Box, Button, Link as MuiLink, Portal, Stack } from '@mui/material';
import {
	GridColDef,
	GridFilterModel,
	GridInitialState,
	GridPaginationModel,
	GridSlotProps,
	GridSlots,
	PropsFromSlot,
} from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { Link as ReactRouterLink } from 'react-router-dom';
import useSWR from 'swr';

import { DataGrid, DataGridToolbar, GridActionsCellItem } from '~components';
import { pagePathSegments } from '~constants';
import { useAuthorize } from '~features/authentication';
import { BaseReference } from '~interfaces';
import { MutationRefProps } from '~interfaces/refProps';

import SkcDeviceGroup from '../../interfaces/skcDeviceGroup';
import SkcDeviceGroupsService from '../../services/skcDeviceGroupsService';

const toolbarIdString = 'topology-target-datagrid';

const service = new SkcDeviceGroupsService();

interface SkcDeviceGroupsDataGridProps {
	onEdit?: (value: BaseReference) => void;
	onDelete?: (value: BaseReference) => void;
	onCreate?: () => void;
}

const SkcDeviceGroupsDataGrid = forwardRef<MutationRefProps, SkcDeviceGroupsDataGridProps>(
	({ onEdit, onDelete, onCreate }, ref) => {
		const { t } = useTranslation();
		const { isSuperAdmin } = useAuthorize();

		const columns = useMemo<GridColDef<SkcDeviceGroup>[]>(
			() => [
				{
					field: 'id',
					headerName: 'ID',
					width: 80,
					hideable: false,
					renderCell: (params) => (
						<MuiLink component={ReactRouterLink} to={params.value.toString()}>
							{params.value}
						</MuiLink>
					),
				},
				{
					field: 'label',
					headerName: t('ui.label.name'),
					flex: 1,
				},
				{
					field: 'description',
					headerName: t('ui.label.description'),
					valueFormatter: (value: string) => value ?? '-',
					flex: 1,
				},
				{
					field: 'organisation',
					headerName: t('organisation'),
					// valueFormatter: (value: BaseReference) => value?.label ?? '-',
					renderCell: (params) =>
						params.value != null ?
							<MuiLink
								component={ReactRouterLink}
								to={`/${path.join(pagePathSegments.Organisations, params.value.id.toString(), pagePathSegments.Summary)}`}
							>
								{params.value.label}
							</MuiLink>
						:	'-',
					flex: 1,
				},
				{
					field: 'devicesCount',
					headerName: t('devices'),
					valueGetter: (_, row) => row.devices?.length ?? 0,
					type: 'number',
					width: 180,
				},
				{
					field: 'actions',
					type: 'actions',
					display: 'text',
					width: 50,
					resizable: false,
					align: 'right',
					hideable: false,
					getActions: (params) => [
						<GridActionsCellItem
							key={`${params.id}-edit`}
							label={t('ui.edit')}
							icon={<EditIcon />}
							color='inherit'
							onClick={() => onEdit?.({ id: params.id.toString(), label: params.row.label })}
							showInMenu
						/>,
						<GridActionsCellItem
							key={`${params.id}-delete`}
							label={t('ui.delete')}
							icon={<DeleteOutlinedIcon color='error' />}
							color='error'
							onClick={() => onDelete?.({ id: params.id.toString(), label: params.row.label })}
							showInMenu
						/>,
					],
				},
			],
			[t, onDelete, onEdit]
		);

		const [fetchParameters, setFetchParameters] = useState<object>({});
		const { data, isLoading, isValidating, error, mutate } = useSWR(
			[service.basePath, fetchParameters],
			([_, args]) => {
				if (isLoading || isValidating) {
					service.abortCurrentRequest('Parameters change');
				}

				return service.getDeviceGroups(args);
			},
			{
				keepPreviousData: true,
			}
		);

		const [totalCount, setTotalCount] = useState<number>(data?.total || 0);

		useEffect(() => {
			if (data?.total != null) {
				setTotalCount(data.total);
			}
		}, [data]);

		useImperativeHandle(ref, () => ({
			mutate: mutate,
		}));

		const handleStateRestore = (state: GridInitialState) => {
			const searchQuery = state.filter?.filterModel?.quickFilterValues?.[0];
			const organisation = state.filter?.filterModel?.items.find(
				(el) => el.field === 'organisation'
			)?.value;

			setFetchParameters((prev) => ({
				...prev,
				page:
					state.pagination?.paginationModel?.page != null ?
						state.pagination.paginationModel.page + 1
					:	undefined,
				pageSize: state.pagination?.paginationModel?.pageSize,
				organisationId: organisation?.id,
				searchQuery: searchQuery,
			}));
		};

		const handlePaginationChange = (model: GridPaginationModel) => {
			setFetchParameters((prev) => ({
				...prev,
				page: model.page + 1,
				pageSize: model.pageSize,
			}));
		};

		const handleFilterModelChange = (model: GridFilterModel) => {
			const searchQuery = model.quickFilterValues?.[0];
			const organisation = model.items.find((el) => el.field === 'organisation')?.value;

			setFetchParameters((prev) => ({
				...prev,
				organisationId: organisation?.id,
				searchQuery: searchQuery,
			}));
		};

		const getTogglableColumns = (columns: GridColDef[]): string[] => {
			const hiddenFields = isSuperAdmin() ? [] : ['organisation'];
			const visibleColumns = columns.filter(
				(column) => column.hideable && !hiddenFields.includes(column.field)
			);
			return visibleColumns.map((column) => column.field);
		};

		return (
			<Stack
				spacing={2}
				sx={{
					display: 'flex',
					height: 1,
				}}
			>
				<Box
					id={toolbarIdString}
					sx={{
						minHeight: 48,
					}}
				/>
				<DataGrid
					disableColumnSorting
					snapshotKey='accdg'
					columns={columns}
					rows={data?.results ?? []}
					rowCount={totalCount}
					loading={isLoading || isValidating}
					error={error}
					onPaginationModelChange={handlePaginationChange}
					onFilterModelChange={handleFilterModelChange}
					onStateRestore={handleStateRestore}
					// columnVisibilityModel={columnVisibilityModel}
					// onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
					slots={{
						toolbar: Toolbar as GridSlots['toolbar'],
					}}
					slotProps={{
						toolbar: {
							onCreate: onCreate,
						} as GridSlotProps['toolbar'],
						columnsManagement: {
							getTogglableColumns,
						},
						noRowsOverlay: {
							title: t('noResourceFound', { resource: t('deviceGroups') }),
						},
						noResultsOverlay: {
							title: t('noResourceFound', { resource: t('deviceGroups') }),
						},
					}}
					sx={{ flexGrow: 1, flexBasis: 200 }}
				/>
			</Stack>
		);
	}
);

interface ToolbarProps extends PropsFromSlot<GridSlots['toolbar']> {
	onCreate?: () => void;
}

const Toolbar = (props: ToolbarProps) => {
	const { t } = useTranslation();

	return (
		<Portal container={() => document.getElementById(toolbarIdString)}>
			<DataGridToolbar organisationSyncMode='url'>
				<Button variant='contained' onClick={() => props.onCreate?.()}>
					{t('addResource', { resource: t('deviceGroup') })}
				</Button>
			</DataGridToolbar>
		</Portal>
	);
};

export default SkcDeviceGroupsDataGrid;
